/**
 * NotificationsStep Component
 *
 * Step 3 of the onboarding wizard - configure notification preferences.
 * Simplified for onboarding - focuses on essentials:
 * - Alert level filters (which alerts to receive)
 * - Email verification flow using Recipients API (consistent with Settings)
 *
 * Advanced settings (quiet hours, digest timing) can be configured
 * in Settings after completing onboarding.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  BlockStack,
  InlineStack,
  Text,
  Icon,
  Box,
  Select,
  TextField,
  Button,
  Banner,
  Spinner,
} from '@shopify/polaris';
import {
  NotificationIcon,
  EmailIcon,
  InfoIcon,
  CheckIcon,
} from '@shopify/polaris-icons';
import { AlertLevelSelector, type AlertLevel } from '../settings/selectors';
import { useAuthenticatedFetch } from '~/hooks/shopify';

/** Email recipient type from Recipients API */
interface EmailRecipient {
  id: number;
  shop_id: number;
  recipient_name: string;
  email_address: string;
  alert_levels: string[];
  is_verified: boolean;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationsStepProps {
  /** Optional callback when notification settings change */
  onSettingsChange?: (settings: {
    alertLevelFilters: AlertLevel[];
    emailEnabled: boolean;
    emailAddress: string;
    isVerified: boolean;
  }) => void;
}

export function NotificationsStep({ onSettingsChange }: NotificationsStepProps) {
  // Alert level filters (which alerts to receive)
  const [alertLevelFilters, setAlertLevelFilters] = useState<AlertLevel[]>(['urgent', 'warning']);

  // Email configuration
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailAddress, setEmailAddress] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedRecipients, setVerifiedRecipients] = useState<EmailRecipient[]>([]);
  const [showNewEmail, setShowNewEmail] = useState(false);
  
  // New state for Recipients API flow
  const [currentRecipientId, setCurrentRecipientId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get authenticated fetch function - returns null until App Bridge is ready
  const authenticatedFetch = useAuthenticatedFetch();

  // Notify parent of settings changes
  const notifyParent = useCallback(() => {
    onSettingsChange?.({
      alertLevelFilters,
      emailEnabled,
      emailAddress,
      isVerified,
    });
  }, [alertLevelFilters, emailEnabled, emailAddress, isVerified, onSettingsChange]);

  // Fetch verified recipients on component mount using Recipients API
  useEffect(() => {
    if (!authenticatedFetch) return; // Wait for App Bridge
    
    const fetchRecipients = async () => {
      try {
        const response = await authenticatedFetch('/api/recipients?verified=true');
        if (response.ok) {
          const data = (await response.json()) as { success: boolean; recipients: EmailRecipient[] };
          if (data.success && data.recipients.length > 0) {
            setVerifiedRecipients(data.recipients);
            setEmailAddress(data.recipients[0].email_address);
            setCurrentRecipientId(data.recipients[0].id);
            setIsVerified(true);
          } else {
            setShowNewEmail(true);
          }
        }
      } catch {
        setShowNewEmail(true);
      }
    };
    fetchRecipients();
  }, [authenticatedFetch]);

  // Notify parent when settings change
  useEffect(() => {
    notifyParent();
  }, [notifyParent]);

  // Create recipient and send verification using Recipients API
  const handleSendVerification = async () => {
    if (!authenticatedFetch) {
      setError('App is still loading. Please wait a moment and try again.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Step 1: Create a new recipient
      const createResponse = await authenticatedFetch('/api/recipients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_name: emailAddress.split('@')[0], // Use email prefix as name
          email_address: emailAddress,
          alert_levels: alertLevelFilters,
        }),
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        // Handle duplicate email gracefully - find existing recipient
        if (createResponse.status === 409) {
          // Email already exists, try to find it
          const listResponse = await authenticatedFetch('/api/recipients');
          if (listResponse.ok) {
            const listData = (await listResponse.json()) as { recipients: EmailRecipient[] };
            const existingRecipient = listData.recipients.find(
              (r) => r.email_address.toLowerCase() === emailAddress.toLowerCase()
            );
            if (existingRecipient) {
              if (existingRecipient.is_verified) {
                setCurrentRecipientId(existingRecipient.id);
                setIsVerified(true);
                setIsLoading(false);
                return;
              }
              // Recipient exists but not verified, send verification
              setCurrentRecipientId(existingRecipient.id);
              await sendVerificationToRecipient(existingRecipient.id);
              return;
            }
          }
        }
        setError(errorData.error || 'Failed to create email recipient. Please try again.');
        setIsLoading(false);
        return;
      }
      
      const createData = (await createResponse.json()) as { success: boolean; recipient: EmailRecipient };
      const recipientId = createData.recipient.id;
      setCurrentRecipientId(recipientId);
      
      // Step 2: Send verification email
      await sendVerificationToRecipient(recipientId);
    } catch {
      setError('Failed to send verification email. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Helper function to send verification to a specific recipient
  const sendVerificationToRecipient = async (recipientId: number) => {
    if (!authenticatedFetch) return;
    
    try {
      const verifyResponse = await authenticatedFetch(`/api/recipients/${recipientId}/send-verification`, {
        method: 'POST',
      });
      
      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        setError(errorData.error || 'Failed to send verification email. Please try again.');
        setIsLoading(false);
        return;
      }
      
      setVerificationSent(true);
      setIsLoading(false);
    } catch {
      setError('Failed to send verification email. Please try again.');
      setIsLoading(false);
    }
  };

  // Verify code using Recipients API
  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) return;
    if (!authenticatedFetch || !currentRecipientId) {
      setError('App is still loading. Please wait a moment and try again.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authenticatedFetch(`/api/recipients/${currentRecipientId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: verificationCode }),
      });
      
      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        setError(errorData.error || 'Invalid verification code. Please try again.');
        setIsLoading(false);
        return;
      }
      
      setIsVerified(true);
      setIsLoading(false);
    } catch {
      setError('Failed to verify code. Please try again.');
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setVerificationCode('');
    setVerificationSent(false);
    setError(null);
    if (currentRecipientId) {
      setIsLoading(true);
      await sendVerificationToRecipient(currentRecipientId);
    } else {
      await handleSendVerification();
    }
  };

  const handleAlertLevelsChange = (levels: AlertLevel[]) => {
    setAlertLevelFilters(levels);
  };

  return (
    <BlockStack gap="500">
      {/* Header - matches ThresholdStep style */}
      <InlineStack gap="200" blockAlign="center">
        <Box>
          <Icon source={NotificationIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            Set up notifications
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Choose which alerts to receive and verify your email
          </Text>
        </BlockStack>
      </InlineStack>

      {/* Alert Level Filters - compact grid layout */}
      <BlockStack gap="300">
        {/* <Text as="p" variant="bodyMd" fontWeight="semibold">
          Alert levels to receive
        </Text> */}

        <AlertLevelSelector
          selectedLevels={alertLevelFilters}
          onChange={handleAlertLevelsChange}
          layout="grid"
        />

        {alertLevelFilters.length === 0 && (
          <Banner tone="critical">
            <Text as="p" variant="bodySm">
              Select at least one alert level to receive notifications.
            </Text>
          </Banner>
        )}
      </BlockStack>

      {/* Email Section - streamlined */}
      <BlockStack gap="300">
        <InlineStack gap="200" blockAlign="start">
          <Box>
            <Icon source={EmailIcon} tone="subdued" />
          </Box>
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            Email notifications
          </Text>
        </InlineStack>

        {/* Email already verified - show success state */}
        {isVerified ? (
          <Box
            background="bg-surface-success"
            padding="400"
            borderRadius="200"
            borderColor="border-success"
            borderWidth="025"
          >
            <InlineStack gap="300" blockAlign="center">
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--p-color-bg-fill-success)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon source={CheckIcon} tone="success" />
              </div>
              <BlockStack gap="050">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  Email verified
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {emailAddress}
                </Text>
              </BlockStack>
            </InlineStack>
          </Box>
        ) : (
          <Box
            background="bg-surface"
            padding="400"
            borderRadius="200"
            borderColor="border"
            borderWidth="025"
          >
            <BlockStack gap="300">
              {verifiedRecipients.length > 0 && !showNewEmail ? (
                <Select
                  label="Select a verified email"
                  labelHidden
                  options={[
                    ...verifiedRecipients.map((recipient) => ({ label: recipient.email_address, value: recipient.email_address })),
                    { label: 'Use a different email', value: '__new__' },
                  ]}
                  value={emailAddress}
                  onChange={(value) => {
                    if (value === '__new__') {
                      setShowNewEmail(true);
                      setEmailAddress('');
                      setIsVerified(false);
                    } else {
                      setEmailAddress(value);
                      setIsVerified(true);
                    }
                  }}
                />
              ) : !verificationSent ? (
                <BlockStack gap="200">
                  {/* Error Banner */}
                  {error && (
                    <Banner tone="critical" onDismiss={() => setError(null)}>
                      <Text as="p" variant="bodySm">{error}</Text>
                    </Banner>
                  )}
                  <Text as="p" variant="bodySm" tone="subdued">
                    Enter your email to receive stock alerts
                  </Text>
                  <InlineStack gap="200" blockAlign="start">
                    <Box width="100%">
                      <TextField
                        label="Email"
                        labelHidden
                        type="email"
                        value={emailAddress}
                        onChange={setEmailAddress}
                        placeholder="email@example.com"
                        autoComplete="email"
                        disabled={isLoading}
                      />
                    </Box>
                    <Button onClick={handleSendVerification} disabled={!emailAddress || isLoading} loading={isLoading}>
                      Send code
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  {/* Error Banner */}
                  {error && (
                    <Banner tone="critical" onDismiss={() => setError(null)}>
                      <Text as="p" variant="bodySm">{error}</Text>
                    </Banner>
                  )}
                  <Banner tone="info">
                    <Text as="p" variant="bodySm">
                      Enter the 6-digit code sent to <strong>{emailAddress}</strong>
                    </Text>
                  </Banner>
                  <InlineStack gap="200" blockAlign="start">
                    <Box width="100%">
                      <TextField
                        label="Verification code"
                        labelHidden
                        type="text"
                        value={verificationCode}
                        onChange={setVerificationCode}
                        placeholder="000000"
                        maxLength={6}
                        autoComplete="off"
                        disabled={isLoading}
                      />
                    </Box>
                    <Button
                      variant="primary"
                      onClick={handleVerifyCode}
                      disabled={verificationCode.length !== 6 || isLoading}
                      loading={isLoading}
                    >
                      Verify
                    </Button>
                  </InlineStack>
                  <Button variant="plain" onClick={handleResendCode} disabled={isLoading}>
                    Resend code
                  </Button>
                </BlockStack>
              )}
            </BlockStack>
          </Box>
        )}
      </BlockStack>

      {/* Info note - compact */}
      <Box background="bg-surface-secondary" paddingBlock="200" paddingInline="300" borderRadius="200">
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Box>
            <Icon source={InfoIcon} tone="subdued" />
          </Box>
          <Text as="p" variant="bodySm" tone="subdued">
            Configure Slack, browser notifications, and webhooks in Settings after setup.
          </Text>
        </InlineStack>
      </Box>
    </BlockStack>
  );
}
